import { BreadcrumbJsonLd } from 'next-seo';
import PropTypes from 'prop-types';

import FaqJsonLd from 'components/Seo/FaqJsonLd';
import ItemListJsonLd from 'components/Seo/ItemListJsonLd';
import SchemaJsonLd from 'components/Seo/SchemaJsonLd';

import { getOfferImageLink } from 'lib/image';
import { toAbsoluteLink, toOfferLink, toStoreOffersLink } from 'lib/links';
import { toFormattedOfferOldPrice } from 'lib/offer';
import { toFormattedOfferPrice } from 'lib/offerPage';
import {
  formatItemListAsJsonLd,
  formatProductAsJsonLd,
  fromStatusToAvailabilityJsonLd,
  getCategoryBreadcrumbArr,
} from 'lib/schemas';
import { toFormattedJsonLdString } from 'lib/string';

const CollectionPageSchema = ({ canonicalUrl, itemListSchemaId, name }) => (
  <SchemaJsonLd
    id="category-collection-page"
    schema={{
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name,
      url: canonicalUrl,
      mainEntity: { '@type': 'ItemList', '@id': itemListSchemaId },
    }}
  />
);

CollectionPageSchema.propTypes = {
  canonicalUrl: PropTypes.string.isRequired,
  itemListSchemaId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

const WebPageSchema = ({ canonicalUrl, categoryImage, name, serverDate }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    url: canonicalUrl,
  };

  if (serverDate) {
    schema.lastReviewed = serverDate;
  }

  if (categoryImage) {
    schema.primaryImageOfPage = {
      '@type': 'ImageObject',
      url: categoryImage,
    };
  }

  return <SchemaJsonLd id="category-webpage" schema={schema} />;
};

WebPageSchema.propTypes = {
  canonicalUrl: PropTypes.string.isRequired,
  categoryImage: PropTypes.string,
  name: PropTypes.string.isRequired,
  serverDate: PropTypes.string,
};

const CategorySchema = ({
  canonicalUrl,
  categoryImage = null,
  categoryName,
  categorySlug,
  deepSubcategoryName = null,
  deepSubcategorySlug = null,
  faqItems = [],
  serverDate = null,
  subcategoryName = null,
  subcategorySlug = null,
  offers,
}) => {
  // Subcategory pages compose the same schema; keep CollectionPage/WebPage
  // scoped to the deepest level so that name/url match the rendered URL.
  const resolvedName = deepSubcategoryName || subcategoryName || categoryName;
  const itemListSchemaId = `${canonicalUrl}#category-offers`;

  return (
    <>
      <CollectionPageSchema
        canonicalUrl={canonicalUrl}
        itemListSchemaId={itemListSchemaId}
        name={resolvedName}
      />
      <WebPageSchema
        canonicalUrl={canonicalUrl}
        categoryImage={categoryImage}
        name={resolvedName}
        serverDate={serverDate}
      />
      {faqItems?.length > 0 && <FaqJsonLd faqItems={faqItems} />}
      <BreadcrumbJsonLd
        itemListElements={getCategoryBreadcrumbArr({
          categoryName,
          categorySlug,
          subcategoryName,
          subcategorySlug,
          deepSubcategoryName,
          deepSubcategorySlug,
        })}
      />
      <ItemListJsonLd
        description={`Melhores ofertas em ${resolvedName}`}
        id="category-offers"
        items={offers.slice(0, 10)}
        name={`${resolvedName} - Promoções`}
        renderItemAsSchema={(
          {
            offerId,
            offerOldPrice,
            offerPhoto,
            offerPrice,
            offerPriceType,
            offerSlug,
            offerStatusName,
            offerTags,
            offerTitle,
            storeName,
            storeSlug,
          },
          index
        ) =>
          formatItemListAsJsonLd({
            item: formatProductAsJsonLd({
              images: [getOfferImageLink(offerPhoto, 268)],
              offers: [
                {
                  price: offerPrice,
                  priceCurrency: 'BRL',
                  availability: fromStatusToAvailabilityJsonLd(
                    offerStatusName,
                    offerTags
                  ),
                  url: toAbsoluteLink(toOfferLink(offerSlug)),
                  seller: {
                    name: storeName,
                    url: toAbsoluteLink(toStoreOffersLink(storeSlug)),
                  },
                  offerCount: 1,
                  category: categoryName,
                  lowPrice: toFormattedOfferPrice(offerPrice, offerPriceType),
                  highPrice: toFormattedOfferOldPrice(
                    offerOldPrice,
                    offerPriceType
                  ),
                },
              ],
              productName: toFormattedJsonLdString(offerTitle),
              sku: offerId,
            }),
            position: index + 1,
            url: toAbsoluteLink(toOfferLink(offerSlug)),
          })
        }
        schemaId={itemListSchemaId}
      />
    </>
  );
};

CategorySchema.propTypes = {
  // Required: caller is responsible for the canonical URL of the rendered
  // page. Subcategory routes (`/promocoes/{slug}/s/`) and root category
  // routes (`/promocoes/{slug}/`) generate different URLs, so a fallback
  // based on `categorySlug` alone produces wrong schema URLs on subcategory
  // pages (name=Notebooks but url=/promocoes/eletronicos/).
  canonicalUrl: PropTypes.string.isRequired,
  categoryImage: PropTypes.string,
  categoryName: PropTypes.string.isRequired,
  categorySlug: PropTypes.string.isRequired,
  deepSubcategoryName: PropTypes.string,
  deepSubcategorySlug: PropTypes.string,
  faqItems: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ),
  serverDate: PropTypes.string,
  subcategoryName: PropTypes.string,
  subcategorySlug: PropTypes.string,
  offers: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.number,
      categoryName: PropTypes.string,
      categorySlug: PropTypes.string,
      key: PropTypes.string.isRequired,
      offerComments: PropTypes.number.isRequired,
      offerId: PropTypes.number.isRequired,
      offerIsHighlight: PropTypes.bool.isRequired,
      ratings: PropTypes.shape().isRequired,
      offerOldPrice: PropTypes.number,
      offerPhoto: PropTypes.string.isRequired,
      offerPrice: PropTypes.number.isRequired,
      offerPriceType: PropTypes.string.isRequired,
      offerPublished: PropTypes.string.isRequired,
      offerSlug: PropTypes.string.isRequired,
      offerStatusName: PropTypes.string.isRequired,
      offerUserVisibility: PropTypes.string.isRequired,
      offerTags: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        })
      ),
      offerTitle: PropTypes.string.isRequired,
      storeDomain: PropTypes.string.isRequired,
      storeId: PropTypes.number,
      subcategoryId: PropTypes.number,
      subcategoryName: PropTypes.string,
      subcategorySlug: PropTypes.string,
    }).isRequired
  ).isRequired,
};

export default CategorySchema;
